import React, { useState, useEffect } from 'react';
import VisualEditor from '../../components/VisualEditor';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import axiosInstance from '../../api/axios';
import { toast } from 'sonner';

const NewsletterVisualEditor = () => {
  const [settings, setSettings] = useState({
    title: 'Newsletter abonnieren',
    subtitle: 'Bleiben Sie auf dem Laufenden!',
    button_text: 'Abonnieren',
    background_color: '#ffffff',
    text_color: '#000000',
    button_color: '#C4A084',
    show_on_load: true,
    delay_seconds: 3
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get('/settings/newsletter-popup-settings');
      if (response.data && response.data.id) {
        setSettings(response.data);
      }
    } catch (error) {
      console.error('Error fetching newsletter settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      await axiosInstance.put('/settings/newsletter-popup-settings', settings);
      toast.success('Newsletter-Einstellungen gespeichert & veröffentlicht!');
    } catch (error) {
      console.error('Error saving newsletter settings:', error);
      toast.error('Fehler beim Speichern');
    }
  };

  // Newsletter Preview Component
  const NewsletterPreview = () => (
    <Card className="max-w-md mx-auto" style={{ backgroundColor: settings.background_color }}>
      <CardContent className="p-8 text-center">
        <h2 
          className="text-2xl font-bold mb-2 editable-title" 
          style={{ color: settings.text_color }}
          data-field="title"
        >
          {settings.title}
        </h2>
        <p 
          className="text-gray-600 mb-6 editable-subtitle" 
          style={{ color: settings.text_color, opacity: 0.8 }}
          data-field="subtitle"
        >
          {settings.subtitle}
        </p>
        
        <div className="space-y-3">
          <Input
            type="email"
            placeholder="Ihre E-Mail-Adresse"
            className="text-center"
          />
          <button
            className="w-full py-3 px-6 rounded-lg font-semibold text-white transition-colors editable-button"
            style={{ backgroundColor: settings.button_color }}
            data-field="button_text"
          >
            {settings.button_text}
          </button>
        </div>
        
        <p className="text-xs text-gray-500 mt-4" style={{ color: settings.text_color, opacity: 0.6 }}>
          Sie können sich jederzeit abmelden
        </p>
      </CardContent>
    </Card>
  );

  // Global Settings Component
  const GlobalSettings = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div>
        <label className="block text-sm font-medium mb-2">Hintergrundfarbe</label>
        <div className="flex gap-2">
          <input
            type="color"
            value={settings.background_color}
            onChange={(e) => setSettings({ ...settings, background_color: e.target.value })}
            className="w-12 h-10"
          />
          <Input
            value={settings.background_color}
            onChange={(e) => setSettings({ ...settings, background_color: e.target.value })}
            placeholder="#ffffff"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">Button-Farbe</label>
        <div className="flex gap-2">
          <input
            type="color"
            value={settings.button_color}
            onChange={(e) => setSettings({ ...settings, button_color: e.target.value })}
            className="w-12 h-10"
          />
          <Input
            value={settings.button_color}
            onChange={(e) => setSettings({ ...settings, button_color: e.target.value })}
            placeholder="#C4A084"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">Verzögerung (Sekunden)</label>
        <Input
          type="number"
          min="0"
          max="60"
          value={settings.delay_seconds}
          onChange={(e) => setSettings({ ...settings, delay_seconds: parseInt(e.target.value) || 0 })}
        />
      </div>

      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          id="show_on_load"
          checked={settings.show_on_load}
          onChange={(e) => setSettings({ ...settings, show_on_load: e.target.checked })}
          className="rounded"
        />
        <label htmlFor="show_on_load" className="text-sm font-medium">
          Beim Seitenaufruf anzeigen
        </label>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>Lädt...</p>
      </div>
    );
  }

  return (
    <div className="h-screen">
      <VisualEditor
        title="Newsletter-Popup bearbeiten"
        previewComponent={<NewsletterPreview />}
        onSave={handleSave}
        globalSettings={<GlobalSettings />}
      />
    </div>
  );
};

export default NewsletterVisualEditor;
